//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardHeader,
  IconButton,
} from '@mui/material';
import {
  InsertComment,
  Cancel, CheckCircle, Gavel, CircleOutlined,
  ExpandLess, ExpandMore,
} from '@mui/icons-material';

// === API ===
import { onCorrectionMode, overruleFileException, removeListener, requestCorrectionMode, updateFileRuleComment } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===
import { Field } from '../../components/Field/Field';
import { CommentModal } from '../../components/CommentModal/CommentModal';
import { Spacer } from 'components/Spacer';

// === HOOKS ===
import { useAnalytics } from 'pages/DocAnalytics/AnalyticsContext/AnalyticsContext';

//#endregion

//-----------------------------------------------------------------
export function Exception( { data } ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const ref = useRef();

  const theme = useTheme();

  const { review_flags } = useAnalytics();

  const [ mode     , setMode    ] = useState( false );
  const [ is_open  , setIsOpen  ] = useState( false );
  const [ is_open2 , setIsOpen2 ] = useState( false );
  const [ show     , setShow    ] = useState( false );
  const [ status   , setStatus  ] = useState( data.status  );
  const [ comment  , setComment ] = useState( data.comment );

  const is_correct_all = mode === 'CORRECT-ALL';

  const is_passed = [1,3].includes( status );

  const _status_icons = {
    0 : <Cancel color='error'/>,
    1 : <CheckCircle color='success'/>,
    2 : <Gavel color='success'/>,
    3 : <CircleOutlined color='success'/>,
  }

  const expand_icon = show ? <ExpandLess fontSize='large' /> : <ExpandMore fontSize='large' />;

  //#endregion

  //#region [STYLES]

  const _styles = {
    field_paper: {
      gridColumn: 'span 4',
      overflow : 'auto'     ,
      position : 'relative' ,
      width    : '100%',
      boxShadow: 0,
      overflow: 'hidden',
    },
    document_container: {
      width: '100%',
      overflow: 'hidden',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],

      scrollbarWidth: "none", // Hide the scrollbar for firefox
      '&::-webkit-scrollbar': {
          display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
      },
      '&-ms-overflow-style:': {
          display: 'none', // Hide the scrollbar for IE
      },
    },
    card: {
      variant: 'primary',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    header: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    footer: {
      display: 'block',
      height: '4rem',
      padding: '0.5rem',
      paddingLeft: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    overrule: {
      display: is_passed ? 'none' : '',
    },
    comment: {
      display: '',
    },
  }

  const
    CONTAINER = {
      ref: ref,
      sx : _styles.field_paper,
    },
    CARD = {
      variant: 'primary',
      sx: {
        overflow: 'hidden',
        backgroundColor: theme.palette.background.primary[theme.palette.mode],
        marginRight: '3px',
      }
    },
    HEADER = {
      sx: _styles.header,
      title: data.id + '. ' + data.description,
      children: data.id + '. ' + data.description
    },
    FOOTER = {
      sx: _styles.footer,
    },
    BUTTON_OVERRULE = {
      sx       : _styles.overrule,
      variant  : 'contained',
      color    : 'primary',
      onClick: () => { setIsOpen2(true) },
      children : 'OVERRULE',
      name     : 'da_overrule_exceptions_btn',
    },
    COMMENT = {
      sx       : _styles.comment,
      onClick: () => { setIsOpen(true) },
      name     :'da_comment_exceptopns_btn'
    };

  //#endregion

  //#region [EFFECT]

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onModalClose( event ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen (false)
      setIsOpen2(false)
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onOverrideModalConfirm( text ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen2(false)
      overruleFileException( data.id, text );
      setStatus( 2 );
      setComment( text );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onCommentModalConfirm( text ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen(false)
      updateFileRuleComment( data.id, text );
      setComment( text );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_Open( event ) {
    //-----------------------------------------------------------------
    try {
      setShow( !show );
    } catch (error) {
      console.warn(error);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const is_visible = ( status === 0 && review_flags.includes( -1 ) ) || ( status === 1 && review_flags.includes( 1 ) ) || ( status === 2 && review_flags.includes( 2 ) );

    if( !is_visible ) return null;

    let column_count = 1;
    const blocks = data.blocks?.map( (block,row) => {
      // const block = data.blocks.items[block_id];
      const filtered = block.fields;
      column_count = filtered.length;

      if( data.is_table ) {
        return filtered.map( (field, column) => <Field key={field.Name} field={field} row={row+1} column={column+1} />  );
      }
      
      return <Box key={block + row} sx={ {
        display: 'flex',
      } }  name='da_exeptions_field_input'> { filtered.map( (field, column) => <Field key={field.Name} field={field} row={row+1} column={column+1} /> ) } </Box>
    });

    return (
      <Box { ...CONTAINER }>
        <Card { ...CARD }>
          <div style={{ display: 'flex', alignItems: 'center' }} >
            <CardHeader { ...HEADER } />
            <Spacer push_right />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'}}>
              { _status_icons[status] || <div /> }
              <Button sx={{ height: '20px'}} onClick={onClick_Open}>
                {expand_icon}
              </Button>
            </Box>
          </div>
          { show ? 
            <>
              <div style={{ width: '100%', display: 'flex' }} >
                <Box sx={{display: (data.is_table ? 'grid' : ''), gridTemplateColumns: (data.is_table ? `repeat( ${column_count}, 1fr )` : ''), gridTemplateRows: (data.is_table ? `repeat( ${blocks.length}, 1fr )` : ''), width:'100%', overflow:'auto',
                  scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.primary[theme.palette.mode]}`,
                }}>
                  {blocks}
                </Box>
                <Spacer size='6px' />
              </div>
              <Box { ...FOOTER }>
                <CommentModal id={data.id} is_open={is_open2} value={comment} onClose={onModalClose} onConfirm={onOverrideModalConfirm} />
                <CommentModal id={data.id} is_open={is_open } value={comment} onClose={onModalClose} onConfirm={onCommentModalConfirm} />
                <Button { ...BUTTON_OVERRULE } />
                <IconButton {...COMMENT}>
                  <InsertComment />
                </IconButton>
              </Box>
            </> : null
          }
        </Card>
      </Box>
    );
  }

  //#endregion

  return display();
}